import { Loader2 } from 'lucide-react';
import { VideoLightbox } from '..';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

interface AdvisorNotPayingContentProps {
  isLoading: boolean;
  onUpgrade: () => void;
  showVideoModal: boolean;
  onVideoModalChange: (show: boolean) => void;
  features: Array<{ title: string; description: string }>;
  subscriptionStatus?: string;
  hasHadFreeTrial?: boolean;
}

const CALENDLY_DEMO_URL = 'https://calendly.com/d/cxdy-x3d-cqj/waterlily-onboarding-and-training';

const PRICING_INFO = {
  title: 'Start Your 2-Week Free Trial',
  subtitle:
    'Get full access to Waterlily to predict long-term care needs, build detailed care plans, and evaluate self-funding, insurance, and annuity strategies.',
  pricingText:
    'Free for 2 weeks, then just $59 per month, no long-term commitment. Most advisors pay for an entire year with a single client conversation.',
  ctaText: 'Start Free Trial',
  demoText: 'Book Demo',
};

const REACTIVATION_INFO = {
  title: 'Reactivate Your Subscription',
  subtitle:
    'Continue using Waterlily to predict long-term care needs, build detailed care plans, and evaluate self-funding, insurance, and annuity strategies.',
  pricingText:
    'Just $59 per month, no long-term commitment. Most advisors pay for an entire year with a single client conversation.',
  ctaText: 'Reactivate Now',
  demoText: 'Book Demo',
};

export function AdvisorNotPayingContent({
  isLoading,
  onUpgrade,
  showVideoModal,
  onVideoModalChange,
  features,
  subscriptionStatus,
  hasHadFreeTrial,
}: AdvisorNotPayingContentProps) {
  const DEMO_VIDEO_URL = 'https://www.loom.com/embed/49a4f641f8a44840be0a678bfbf9351e';

  const handleBookDemo = () => {
    window.open(CALENDLY_DEMO_URL, '_blank');
  };

  // Determine if this is a reactivation (has had a free trial before)
  const isReactivation = hasHadFreeTrial;
  const contentInfo = isReactivation ? REACTIVATION_INFO : PRICING_INFO;

  return (
    <div
      id="trial-required-content"
      className="bg-primary-50 flex h-full w-full px-4 pb-12 pt-3 sm:px-6 lg:px-8"
    >
      <div className="mx-auto w-full max-w-[680px]">
        <Card className="mt-14 rounded-lg border p-12">
          <CardContent className="mb-24 flex flex-col gap-8 p-0 md:mb-0">
            {/* Header Section */}
            <div className="space-y-4 text-center">
              <h1 className="text-primary-dark text-4xl font-semibold">{contentInfo.title}</h1>
              <p className="text-primary-dark text-base leading-6 text-opacity-70">
                {contentInfo.subtitle}
              </p>
              <div className="bg-primary rounded-lg bg-opacity-10 p-4">
                <p className="text-primary text-base leading-6">
                  {isReactivation ? (
                    <>
                      Just <strong>$59 per month</strong>, no long-term commitment. Most advisors
                      pay for an entire year with a single client conversation.
                    </>
                  ) : (
                    <>
                      <strong>Free for 2 weeks</strong>, then just <strong>$59 per month</strong>,
                      no long-term commitment. Most advisors pay for an entire year with a single
                      client conversation.
                    </>
                  )}
                </p>
              </div>
            </div>

            {/* CTA Section */}
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center sm:gap-3">
              <Button
                onClick={onUpgrade}
                disabled={isLoading}
                className="h-12 w-full sm:w-40"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Processing...
                  </span>
                ) : (
                  contentInfo.ctaText
                )}
              </Button>
              <Button
                onClick={handleBookDemo}
                variant="outline"
                className="h-12 w-full sm:w-40"
              >
                {contentInfo.demoText}
              </Button>
            </div>

            <VideoLightbox
              videoUrl={`${DEMO_VIDEO_URL}?t=0s`}
              isOpen={showVideoModal}
              onOpenChange={onVideoModalChange}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
